/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {useCallback, useEffect, useState} from "react";
import {useTypedSelector} from "helpers/reducers";
import {useDispatch} from "useDispatch";
import {PackageItemPersonType} from "types/enums/PackageItemPersonType";
import {AVAILABLE_DURATIONS_BASIC, AVAILABLE_DURATIONS_EVENTS, DEFAULT_DURATION, eventCategories} from "helpers/config";
import {usePackageItemKartingOptions} from "utils/packageUtils";
import BookingAction from "store/booking/BookingAction";
import {PackagesItemsPerson} from "types/entities/PackagesItems";
import {
    getRecommendedOrHighestAvailablePackageItem,
    getRecommendedOrHighestCommonAvailableDuration,
    isSelectablePackageItem,
    resolveRequestedDuration,
} from "utils/packageDurationUtils";

export const useBookingPackagesRowInitialization = (personIndex?: number, personType: PackageItemPersonType = PackageItemPersonType.Adult) => {
    const dispatch = useDispatch();
    const availablePackageItems = useTypedSelector(state => state.bookingState.bookingProcess.availablePackageItems);
    const selectedPackageItems = useTypedSelector(state => state.bookingState.bookingProcess.selectedPackageItems);
    const selectedPackageCreditItems = useTypedSelector(state => state.bookingState.bookingProcess.selectedPackageCreditItems);
    const selectedTimeslot = useTypedSelector(state => state.bookingState.bookingProcess.selectedTimeslot);

    const {getPackageItemByDuration, setPackageItemHandler} = usePackageItemKartingOptions();

    const filter = useTypedSelector(state => state.bookingState.bookingProcess.filter);
    const isEvent = filter.category ? eventCategories.includes(filter.category) : false;
    const durations = isEvent ? AVAILABLE_DURATIONS_EVENTS : AVAILABLE_DURATIONS_BASIC;

    const [chosenDuration, setChosenDuration] = useState<number | undefined>(undefined);
    const [selectedPackageItem, setSelectedPackageItem] = useState<PackagesItemsPerson | null>(null);

    const findClosestDuration = useCallback(
        (preferred: number | undefined, pType: PackageItemPersonType) => {
            return resolveRequestedDuration(
                preferred,
                durations,
                DEFAULT_DURATION,
                duration => getPackageItemByDuration(duration, pType),
                selectedTimeslot
            );
        },
        [getPackageItemByDuration, selectedTimeslot, durations]
    );

    const handleDurationChange = useCallback(
        (newDuration: number) => {
            const duration = findClosestDuration(newDuration, personIndex !== undefined ? personType : PackageItemPersonType.Adult);
            console.log(duration);
            if (duration !== undefined) {
                setChosenDuration(duration);
                if (personIndex !== undefined) {
                    const packageItem = getPackageItemByDuration(duration, personType);
                    if (packageItem) {
                        setPackageItemHandler(personIndex, packageItem.id, personType, 1);
                        setSelectedPackageItem(packageItem);
                    }
                } else {
                    const newSelectedPackageItems = selectedPackageItems.map(item => {
                        const newTicket = getPackageItemByDuration(duration, item.person_type);
                        return {...item, id: newTicket?.id ?? null};
                    });
                    dispatch(BookingAction.setSelectedPackageItems(newSelectedPackageItems));
                }
            }
        },
        [findClosestDuration, personIndex, personType, getPackageItemByDuration, setPackageItemHandler, selectedPackageItems, dispatch]
    );

    useEffect(() => {
        if (!availablePackageItems) return;

        if (personIndex !== undefined) {
            // Per-person logic (Standard row)
            const itemByPerson = selectedPackageItems.find(item => item.personIndex === personIndex);
            const selectableItems = (availablePackageItems.packageItems[personType] || []).filter(isSelectablePackageItem);
            const selectedAvailablePackage =
                itemByPerson?.id !== null && itemByPerson?.person_type === personType
                    ? selectableItems.find(packageItem => packageItem.id === itemByPerson.id)
                    : undefined;

            if (selectedAvailablePackage) {
                setChosenDuration(selectedAvailablePackage.duration);
                setSelectedPackageItem(selectedAvailablePackage);
                return;
            }

            const packageItem = getRecommendedOrHighestAvailablePackageItem(selectableItems, DEFAULT_DURATION, selectedTimeslot);
            console.log(packageItem?.duration);
            if (packageItem) {
                setChosenDuration(packageItem.duration);
                setSelectedPackageItem(packageItem);

                if (itemByPerson?.id !== packageItem.id || itemByPerson?.person_type !== personType) {
                    setPackageItemHandler(personIndex, packageItem.id, personType, 1);
                }
            }
        } else {
            // Global logic (Events / Family row)
            const representedPersonTypes = Array.from(new Set(selectedPackageItems.map(item => item.person_type)));
            const selectableDurationSets: Set<number>[] = representedPersonTypes.map(
                type => new Set<number>((availablePackageItems.packageItems[type] || []).filter(isSelectablePackageItem).map(item => item.duration))
            );
            const commonDurations =
                selectableDurationSets.length > 0
                    ? Array.from(selectableDurationSets[0]).filter(duration => selectableDurationSets.every(durations => durations.has(duration)))
                    : [];

            if (commonDurations.length === 1) {
                const onlyCommonDuration = commonDurations[0];
                const matchingPackageItems = selectedPackageItems.map(item =>
                    (availablePackageItems.packageItems[item.person_type] || []).find(
                        packageItem => packageItem.duration === onlyCommonDuration && isSelectablePackageItem(packageItem)
                    )
                );

                if (matchingPackageItems.every(packageItem => packageItem !== undefined)) {
                    setChosenDuration(onlyCommonDuration);

                    const selectionChanged = selectedPackageItems.some((item, index) => item.id !== matchingPackageItems[index]?.id);
                    if (selectionChanged) {
                        dispatch(
                            BookingAction.setSelectedPackageItems(
                                selectedPackageItems.map((item, index) => ({...item, id: matchingPackageItems[index]?.id ?? item.id}))
                            )
                        );
                    }
                    return;
                }
            }

            const selectedAvailablePackageItems = selectedPackageItems.map(item =>
                item.id !== null
                    ? (availablePackageItems.packageItems[item.person_type] || []).find(
                          packageItem => packageItem.id === item.id && isSelectablePackageItem(packageItem)
                      )
                    : undefined
            );
            const selectedAvailableDuration = selectedAvailablePackageItems[0]?.duration;
            const isInitialized =
                selectedPackageItems.length > 0 &&
                selectedAvailableDuration !== undefined &&
                selectedAvailablePackageItems.every(packageItem => packageItem?.duration === selectedAvailableDuration);

            if (isInitialized) {
                setChosenDuration(selectedAvailableDuration);
            } else if (selectedPackageCreditItems.length > 0 && !selectedPackageItems.some(item => item.id !== null)) {
                return;
            } else {
                const duration = getRecommendedOrHighestCommonAvailableDuration(
                    representedPersonTypes.map(type => availablePackageItems.packageItems[type] || []),
                    DEFAULT_DURATION,
                    selectedTimeslot
                );
                if (duration !== undefined) {
                    setChosenDuration(duration);
                    const newSelectedPackageItems = selectedPackageItems.map(item => {
                        const newTicket = getPackageItemByDuration(duration, item.person_type);
                        return {...item, id: newTicket?.id ?? null};
                    });
                    dispatch(BookingAction.setSelectedPackageItems(newSelectedPackageItems));
                }
            }
        }
    }, [
        availablePackageItems,
        selectedPackageItems,
        selectedPackageCreditItems.length,
        personIndex,
        personType,
        findClosestDuration,
        getPackageItemByDuration,
        setPackageItemHandler,
        dispatch,
    ]);

    return {
        chosenDuration,
        setChosenDuration,
        selectedPackageItem,
        handleDurationChange,
    };
};
